import { ReactNode, useMemo, useState } from "react";
import {
  Input,
  FormLabel,
  FormControl,
  FormHelperText,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
  Textarea,
  FormControlProps,
  Box,
} from "@chakra-ui/react";

type ChangeEvent =
  | Date
  | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

type BaseInputProps = FormControlProps & {
  label?: string;
  type?: string;
  name?: string;
  value?: string;
  message?: string;
  textarea?: boolean;
  placeholder?: string;
  leftElement?: ReactNode;
  rightElement?: ReactNode;
  rows?: number ;
  onChange?: (e: ChangeEvent) => void;
  customInput?: (
    value: any,
    onChange: (e: Date) => void,
    attrs?: any
  ) => ReactNode;
};
const BaseInput: React.FC<BaseInputProps> = ({
  value,
  type,
  label,
  leftElement,
  rightElement,
  placeholder,
  textarea,
  message,
  rows,
  onChange,
  customInput,
  ...props
}) => {
  const [innerValue, setInnerValue] = useState<any>(value || "");
  const [id] = useState(Math.random().toString(36).substring(2, 9));

  const baseStyle = useMemo(
    () => ({
      fontSize: "14px",
      fontFamily: "Inter",
      lineHeight: "20px",
    }),
    []
  );

  const attrs = useMemo(
    () => ({
      id,
      padding: 2,
      fontWeight: 400,
      type: type || "text",
      placeholder: placeholder,
      paddingLeft: leftElement ? 8 : 2,
      ...baseStyle,
    }),
    [id, type, leftElement, placeholder, baseStyle]
  );

  const handleChange = (e: ChangeEvent) => {
    const value = e instanceof Date ? e : e.target.value;
    setInnerValue(value);
    onChange && onChange(e);
  };

  return (
    <FormControl {...props}>
      {label && (
        <FormLabel
          htmlFor={id}
          fontWeight={500}
          color={"#6B7280"}
          {...baseStyle}
        >
          {label}
        </FormLabel>
      )}
      <InputGroup>
        {/* Prepend Icon */}
        {leftElement && (
          <InputLeftElement
            pointerEvents="none"
            children={
              <Text marginTop={"2px"} {...baseStyle}>
                {leftElement}
              </Text>
            }
          />
        )}

        {/* Input */}
        {textarea ? (
          <Textarea
            rows={rows}
            value={innerValue}
            onChange={handleChange}
            {...attrs}
          />
        ) : customInput ? (
          customInput(innerValue, handleChange, attrs)
        ) : (
          <Input value={innerValue} onChange={handleChange} {...attrs} />
        )}

        {/* Append Icon */}
        {rightElement && (
          <InputRightElement
            children={
              <Box marginTop={"2px"} {...baseStyle}>
                {rightElement}
              </Box>
            }
          />
        )}
      </InputGroup>
      {message && <FormHelperText>{message}</FormHelperText>}
    </FormControl>
  );
};

export { BaseInput };
